import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import { Participant } from '@seen/core/models/patients/participant'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Button, TypographyPageTitle } from '@seen/ui'
import {
  IconCalendarEvent,
  IconFileDescription,
  IconPlus,
  IconUser,
} from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy, uniqBy } from 'lodash-es'
import type { JSX } from 'react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { LinkCell } from '#src/components/core/data-table/LinkCell.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function ValidatedToolsPage(): JSX.Element {
  const { patientId } = useParams()
  invariant(patientId, 'Patient ID is required')

  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<Filter[]>([])
  const { data: participant } = useRead(Participant, patientId)
  const { data: validatedToolResponses } = useSearch(
    ValidatedToolResponse,
    participant
      ? ValidatedToolResponse.byParticipant({
          participant: participant.reference,
        })
      : undefined
  )

  const mutate = usePatchMutation<ValidatedToolResponse>().mutate

  const tableData = useMemo(
    () => orderBy(validatedToolResponses ?? [], ['authored'], ['desc']),
    [validatedToolResponses]
  )

  const signedByOptions = useMemo(
    () =>
      uniqBy(
        tableData.flatMap((toolResponse) => {
          if (!toolResponse.author?.reference) {
            return []
          }

          return {
            value: toolResponse.author.reference,
            label:
              referenceDisplay(toolResponse.author) ??
              toolResponse.author.reference,
            keywords: [
              referenceDisplay(toolResponse.author) ??
                toolResponse.author.reference,
            ],
          }
        }),
        'value'
      ),
    [tableData]
  )

  const toolNameOptions = useMemo(
    () =>
      uniqBy(
        tableData.flatMap((toolResponse) => {
          if (!toolResponse.name) {
            return []
          }

          return {
            value: toolResponse.name,
            label: toolResponse.name,
            keywords: [toolResponse.name],
          }
        }),
        'value'
      ),
    [tableData]
  )

  const filterOptions = useMemo<FilterConfigMap<ValidatedToolResponse>>(
    () => ({
      name: {
        label: 'Tool',
        icon: <IconFileDescription className="size-4" />,
        type: 'multi-select',
        options: toolNameOptions,
        valueFn: (toolResponse) => toolResponse.name ?? '',
      },
      signedBy: {
        label: 'Signed by',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: signedByOptions,
        valueFn: (toolResponse) => toolResponse.author?.reference ?? '',
      },
      signedOn: {
        label: 'Signed',
        icon: <IconCalendarEvent className="size-4" />,
        type: 'date',
        valueFn: (toolResponse) => toolResponse.authored ?? '',
      },
    }),
    [signedByOptions, toolNameOptions]
  )

  const filteredTools = useFilteredData(tableData, filterOptions, filters)

  if (!participant || !validatedToolResponses) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Validated Tools</TypographyPageTitle>
        {!isReadOnly && (
          <Combobox
            options={questionnaires.VALIDATED_TOOLS_ARRAY.map((tool) => ({
              value: tool.name,
              label: tool.name,
              keywords: [tool.name],
            }))}
            onSelect={(value) => {
              void navigate(`./${value?.toLowerCase()}`)
            }}
          >
            <Button className="flex items-center gap-2" variant="outline">
              <IconPlus size={16} />
              New validated tool…
            </Button>
          </Combobox>
        )}
      </div>
      <FilterComposer<ValidatedToolResponse>
        id="participant-validated-tools-table-filters"
        filterOptions={filterOptions}
        onChange={setFilters}
      />
      <DataTable
        columns={columns}
        data={filteredTools}
        deleteRowProps={(row) => ({
          title: `Delete "${row.name}"?`,
          description:
            'Deleted validated tools can still be recovered by contacting our team.',
          onConfirm: () => {
            mutate({
              instance: row,
              mutateFn: (x) => {
                x.status = 'entered-in-error'
              },
            })
          },
        })}
      />
    </div>
  )
}

const columns: ColumnDef<ValidatedToolResponse>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <LinkCell to={`./responses/${row.original.id}`}>
        {row.original.name}
      </LinkCell>
    ),
  },
  {
    accessorKey: 'score',
    header: 'Score',
    cell: ({ row }) => (
      <LinkCell to={`./responses/${row.original.id}`}>
        {row.original.score}
      </LinkCell>
    ),
  },
  {
    accessorKey: 'signedBy',
    header: 'Signed by',
    cell: ({ row }) => (
      <LinkCell to={`./responses/${row.original.id}`}>
        <HoverPractitioner practitioner={row.original.author} />
      </LinkCell>
    ),
  },
  {
    accessorKey: 'signedOn',
    header: 'Signed',
    cell: ({ row }) => (
      <LinkCell to={`./responses/${row.original.id}`}>
        {row.original.authored ? (
          <HoverDate date={row.original.authored} />
        ) : null}
      </LinkCell>
    ),
  },
]
